import React, { useEffect, useState } from "react";
import targetApi from "../../services/targetApi";
import achievementApi from "../../services/achievementApi";
import Skeleton from "../../components/common/Skeleton";
import EmptyState from "../../components/common/EmptyState";
import { useToast } from "../../components/common/Toast";
import { FaPaperPlane } from "react-icons/fa";
import "../../styles/target.css";

const todayISO = () => new Date().toISOString().slice(0, 10);

/* =========================================================
   Employee: Submit Achievement
   POST /api/achievements
   Lets the logged-in employee submit completed work against
   one of their own Active targets, and shows their submission
   history (GET /api/achievements/me).
========================================================= */
function SubmitAchievement() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeTargets, setActiveTargets] = useState([]);
  const [myAchievements, setMyAchievements] = useState([]);

  const [form, setForm] = useState({
    target_id: "",
    completed_quantity: "",
    achievement_date: todayISO(),
    remarks: "",
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    setLoading(true);
    Promise.all([targetApi.getMyDashboardTarget(), achievementApi.getMyAchievements()])
      .then(([dashRes, achRes]) => {
        const all = dashRes.data.all_targets || [];
        // 🔗 "Calls" targets are auto-updated from the Call Logs page now,
        // so they're hidden here to avoid double-counting. This form stays
        // for everything else (e.g. Money/Sales targets).
        setActiveTargets(
          all.filter((t) => t.status === "Active" && String(t.target_type).toLowerCase() !== "calls")
        );
        setMyAchievements(achRes.data || []);
      })
      .catch((err) => console.log(err))
      .finally(() => setLoading(false));
  };

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!form.target_id || form.completed_quantity === "" || !form.achievement_date) {
      toast.error("Please fill target, quantity and date");
      return;
    }
    if (isNaN(form.completed_quantity) || Number(form.completed_quantity) < 0) {
      toast.error("Completed quantity must be a valid positive number");
      return;
    }

    setSubmitting(true);
    achievementApi
      .submitAchievement({
        target_id: Number(form.target_id),
        completed_quantity: Number(form.completed_quantity),
        achievement_date: form.achievement_date,
        remarks: form.remarks,
      })
      .then(() => {
        toast.success("Achievement submitted successfully");
        setForm({
          target_id: "",
          completed_quantity: "",
          achievement_date: todayISO(),
          remarks: "",
        });
        loadData();
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Failed to submit achievement");
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <div className="submit-achievement-page">
      <div className="mytarget-header">
        <div>
          <h2>Submit Achievement</h2>
          <p>Log completed work against one of your active targets</p>
        </div>
      </div>

      <p
        style={{
          background: "#eef2ff",
          color: "#3730a3",
          padding: "10px 14px",
          borderRadius: "8px",
          fontSize: "14px",
          margin: "0 0 16px",
        }}
      >
        📞 Your <strong>Calls</strong> target updates automatically every time you add an entry
        on the <strong>Call Logs</strong> page — no need to submit it here.
      </p>

      {loading ? (
        <Skeleton.Card />
      ) : activeTargets.length === 0 ? (
        <EmptyState
          icon="🎯"
          title="No active targets"
          subtitle="You don't have any active target to submit work against."
        />
      ) : (
        <form className="achievement-form" onSubmit={handleSubmit}>
          <div className="form-row">
            <label>Target</label>
            <select name="target_id" value={form.target_id} onChange={handleChange}>
              <option value="">Select a target</option>
              {activeTargets.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.title} ({t.completed_target}/{t.total_target})
                </option>
              ))}
            </select>
          </div>

          <div className="form-row-group">
            <div className="form-row">
              <label>Completed Quantity</label>
              <input
                type="number"
                min="0"
                name="completed_quantity"
                value={form.completed_quantity}
                onChange={handleChange}
                placeholder="e.g. 5"
              />
            </div>
            <div className="form-row">
              <label>Achievement Date</label>
              <input
                type="date"
                name="achievement_date"
                value={form.achievement_date}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row">
            <label>Remarks (optional)</label>
            <textarea
              name="remarks"
              rows={3}
              value={form.remarks}
              onChange={handleChange}
              placeholder="Any notes about this submission..."
            />
          </div>

          <button className="btn-primary" type="submit" disabled={submitting}>
            <FaPaperPlane /> {submitting ? "Submitting..." : "Submit Achievement"}
          </button>
        </form>
      )}

      <div className="mytarget-history">
        <h3>My Submissions</h3>

        {loading ? (
          <Skeleton.Table rows={4} cols={5} />
        ) : myAchievements.length === 0 ? (
          <EmptyState icon="📝" title="No submissions yet" />
        ) : (
          <div className="data-table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Target</th>
                  <th>Completed Qty</th>
                  <th>Remaining</th>
                  <th>Date</th>
                  <th>Remarks</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {myAchievements.map((a) => (
                  <tr key={a.id}>
                    <td>{a.target_title}</td>
                    <td>{a.completed_quantity}</td>
                    <td>{a.remaining_quantity}</td>
                    <td>{new Date(a.achievement_date).toLocaleDateString("en-IN")}</td>
                    <td>{a.remarks || "-"}</td>
                    <td>
                      <span className={`status-pill status-${(a.status || "").toLowerCase()}`}>
                        {a.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default SubmitAchievement;
